import areIntervalsOverlapping from "date-fns/areIntervalsOverlapping";
import parseISO from "date-fns/parseISO";
import { PatientInMerge } from "./types";

// AC10 — mirrors mergePatientHandler's checkout guard, client-side.
export const hasCheckoutAppointment = (patients: PatientInMerge[]): boolean =>
  patients.some((p) => p.appointments.some((a) => a.status === "checkout"));

// AC9 — mirrors checkPatientAppointmentConflicts.ts (backend) exactly.
const conflictIgnoreStatuses = [
  "checkout",
  "incomplete",
  "abandoned",
  "canceled",
  "no_show",
];

const getConflictIntervals = (patients: PatientInMerge[]) => {
  const now = new Date();

  return patients.flatMap((patient) =>
    patient.appointments
      .filter((a) => !conflictIgnoreStatuses.includes(a.status))
      .map((a) => ({
        appointmentId: a.id,
        patientId: patient.id,
        start: parseISO(a.startedAt),
        end: parseISO(a.endedAt),
      }))
      .filter((interval) => interval.start > now),
  );
};

// AC37/38 — the "Conflict" duplicate-group card links to each appointment
// involved in an overlap (Figma node 11515:29993's "View appointment N"
// rows), so callers need the actual appointment ids, not just a boolean.
export const getConflictingAppointmentIds = (
  patients: PatientInMerge[],
): string[] => {
  const intervals = getConflictIntervals(patients);
  const conflictingIds = new Set<string>();

  for (let i = 0; i < intervals.length; i += 1) {
    for (let j = i + 1; j < intervals.length; j += 1) {
      const a = intervals[i];
      const b = intervals[j];
      if (
        a.patientId !== b.patientId &&
        areIntervalsOverlapping(
          { start: a.start, end: a.end },
          { start: b.start, end: b.end },
        )
      ) {
        conflictingIds.add(a.appointmentId);
        conflictingIds.add(b.appointmentId);
      }
    }
  }

  // Stable order — sorted by start time — so "View appointment 1/2/..."
  // numbering doesn't shuffle between renders.
  return intervals
    .filter((interval) => conflictingIds.has(interval.appointmentId))
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map((interval) => interval.appointmentId)
    .filter((id, index, arr) => arr.indexOf(id) === index);
};

export const hasConflictingAppointments = (
  patients: PatientInMerge[],
): boolean => getConflictingAppointmentIds(patients).length > 0;
